import React, {Suspense, useEffect, useRef} from 'react';

import {Outlet, useLocation} from 'react-router-dom';
import {ErrorBoundary as SentryErrorBoundary, setTags} from '@sentry/react';
import type {Scope} from '@sentry/react';

import useRouteLoadingSpan from '../../../hooks/useRouteLoadingSpan';
import Loader from '../Loader';
import ErrorFallbackScreen from './components/ErrorFallbackScreen';

/** First path segment as a stable low-cardinality feature tag ("/" → "dashboard"). */
export const getRouteFeature = (pathname: string): string => pathname.split('/')[1] || 'dashboard';

const tagRouteScope = (scope: Scope) => {
  scope.setTag('error_boundary', 'route');
};

/**
 * Route-level isolation point: a pathless layout route renders this
 * around the active route's element, so a crash inside one route shows a
 * fallback with a way out while the rest of the app (and navigation to every
 * other route) keeps working. Navigating clears a crashed route's fallback
 * instead of leaving it stuck. The root boundary in App.tsx stays as the last
 * resort for crashes outside the route outlet.
 *
 * It also holds the route's loading state: a lazy element suspends here, below
 * the Sentry-traced routes, so a navigation commits its new location (and starts
 * its navigation span) at once and only the element waits behind the Loader.
 */
const RouteLoader = () => {
  useRouteLoadingSpan();
  return <Loader />;
};

const RouteErrorBoundary = () => {
  const location = useLocation();
  const feature = getRouteFeature(location.pathname);
  const taggedFeature = useRef<string | undefined>(undefined);
  const boundary = useRef<SentryErrorBoundary>(null);

  // Reset the caught error on navigation rather than remounting the boundary
  // with `key={pathname}`: a key would also remount the route element itself on
  // a param-only navigation (/draft-editor/1 → /draft-editor/2), where React
  // otherwise reconciles in place and keeps its state. That would silently make
  // DET's own `key={quoteId}` remount redundant and turn every
  // future param-carrying route into a full remount. Resetting while no error
  // is caught would be a wasted render, so it is guarded — `componentStack` is
  // null only in the boundary's initial (healthy) state.
  // Keyed on `location.key`, not `pathname`: brief-mode reads its quote from
  // the query string, so two quotes share one pathname.
  useEffect(() => {
    if (boundary.current?.state.componentStack != null) boundary.current.resetErrorBoundary();
  }, [location.key]);

  // Set during render, not in an effect: a route that throws while rendering is
  // captured in the commit phase, which runs before passive effects flush. An
  // effect here would tag the event with the PREVIOUS route's feature (or leave
  // it unset on first load) — exactly the events the tag exists for. Rendering
  // this boundary happens before its Outlet children, so the tag is in place by
  // the time one of them can throw. The scope-level tag also enriches non-React
  // events (API failures, rejections). Guarded so it runs once per navigation.
  if (taggedFeature.current !== feature) {
    taggedFeature.current = feature;
    setTags({feature});
  }

  return (
    <SentryErrorBoundary
      beforeCapture={tagRouteScope}
      fallback={<ErrorFallbackScreen showNavigation />}
      ref={boundary}
    >
      <Suspense fallback={<RouteLoader />}>
        <Outlet />
      </Suspense>
    </SentryErrorBoundary>
  );
};

export default RouteErrorBoundary;
